import { roundRating } from '@/helpers';
import { _s } from '@/locale';
import { SearchPlace } from '@/types/api/services/search';
import { Place } from '@/types/state/shared';
import * as sanitizeHtml from 'sanitize-html';
import {
  AggregateRating,
  BreadcrumbList,
  GeoCoordinates,
  LocalBusiness,
  PostalAddress,
  Review,
  SearchResultsPage,
  Thing,
  WithContext,
} from 'schema-dts';

const stripHTML = (html: string): string => {
  return sanitizeHtml(html, { allowedTags: [], allowedAttributes: {} });
};

function escapeForJson(input: string): string {
  return JSON.stringify(input).slice(1, -1); // Remove the outer quotes
}

function cleanTitle(input: string): string {
  const stripped = stripHTML(input); // Remove tags
  return escapeForJson(stripped); // Escape remaining dangerous characters
}

function jsonLd<T extends Thing>(json: WithContext<T>): string {
  return JSON.stringify(json);
}

function buildGeo(position: Place['contact']['position']): GeoCoordinates {
  if (!position?.lat && !position?.lon) {
    return undefined;
  }

  return {
    '@type': 'GeoCoordinates',
    latitude: position.lat,
    longitude: position.lon,
  };
}

function buildPostalAddress(address: Place['contact']['address']): PostalAddress {
  const schema: PostalAddress = {
    '@type': 'PostalAddress',
  };

  if (address.street) {
    schema.streetAddress = cleanTitle(address.street);
  }

  if (address.zipcode) {
    schema.postalCode = cleanTitle(address.zipcode);
  }

  if (address.city) {
    schema.addressLocality = cleanTitle(address.city);
  }

  return schema;
}

function buildAggregateRating(reviews: Place['reviews']): AggregateRating {
  const { reviewCount = 0 } = reviews || {};

  if (reviewCount < 1) {
    return undefined;
  }

  if (reviews?.stats?.score) {
    return {
      '@type': 'AggregateRating',
      reviewCount,
      bestRating: 5,
      worstRating: 1,
      ratingValue: roundRating(reviews.stats.score),
    };
  } else {
    return undefined;
  }
}

function buildReview(place: Place): Review[] {
  const schema: Review[] = [];
  const { reviews, about, id } = place || {};

  if (!reviews?.topReviews?.items) {
    return schema;
  }

  reviews.topReviews.items.forEach((item) => {
    const review: Review = {
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: cleanTitle(item.author?.name || _s('Anonymous')),
      },
      publisher: {
        '@type': 'Organization',
        name: 'Bokadirekt',
        url: 'https://www.bokadirekt.se',
      },
      provider: {
        '@type': 'LocalBusiness',
        name: cleanTitle(about.name),
        url: 'https://www.bokadirekt.se/places/' + about.slug + '-' + id,
      },
      datePublished: item.createdAt,
      reviewBody: item.review?.text || '',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: item.review?.score || 0,
        bestRating: 5,
        worstRating: 1,
      },
    };

    schema.push(review);
  });

  return schema;
}

function buildLocalBusinessSchema(place: Place) {
  const { address = {}, phone, email, website, position = {} } = place.contact || {};
  const { reviews = {}, about = {} } = place || {};
  const geo = buildGeo(position);
  const review = buildReview(place);

  const schema: LocalBusiness = {
    '@type': 'LocalBusiness',
    name: cleanTitle(about.name),
    address: buildPostalAddress(address),
    aggregateRating: buildAggregateRating(reviews),
  };

  if (review.length) {
    schema.review = review;
  }

  if (geo) {
    schema.geo = geo;
  }

  if (website) {
    schema.url = cleanTitle(website);
  }

  if (email) {
    schema.email = cleanTitle(email);
  }

  if (phone) {
    schema.telephone = cleanTitle(phone);
  }

  if (about?.description) {
    const description = cleanTitle(about.description);
    if (description) {
      schema.description = description;
    }
  }

  return schema;
}

function searchPlaceAdapter(place: SearchPlace): Place {
  const out: Place = {
    about: { name: place.name },
    contact: place.contact,
    reviews: {
      reviewCount: place?.rating?.count,
      stats: { score: place?.rating?.score },
      topReviews: undefined,
    },
  };

  return out;
}

function buildSearchResultsPageSchema(places: SearchPlace[]) {
  const schema: SearchResultsPage = {
    '@type': 'SearchResultsPage',
    name: 'Local Business Search Results',
    about: 'Search results for local businesses in your area',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: places.map((place, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: buildLocalBusinessSchema(searchPlaceAdapter(place)),
      })),
    },
  };

  return schema;
}

export function getBreadcrumbsSchema(items: { title: string; url: string }[]) {
  const schema: BreadcrumbList = {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: cleanTitle(item.title),
      item: item.url,
    })),
  };

  return jsonLd({
    '@context': 'https://schema.org',
    ...schema,
  });
}

export function getSearchResultsPageSchema(places: SearchPlace[]) {
  const schema = buildSearchResultsPageSchema(places);

  return jsonLd({
    '@context': 'https://schema.org',
    ...schema,
  });
}

export function getLocalBusinessSchema(place: Place) {
  return jsonLd({
    '@context': 'https://schema.org',
    ...buildLocalBusinessSchema(place),
  });
}
